import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Send, Image as ImageIcon, Check } from "lucide-react";
import { toast } from "sonner";

// ---------- Estilos tipo WhatsApp futurista (cristal con el color del colegio) ----------
export const ChatFx = () => (
  <style>{`
    @keyframes zv-in-right { from { opacity:0; transform: translateX(48px) scale(.98); } to { opacity:1; transform:none; } }
    @keyframes zv-in-left { from { opacity:0; transform: translateX(-36px); } to { opacity:1; transform:none; } }
    @keyframes zv-rec { 0%,100% { transform: scale(1); opacity:1; } 50% { transform: scale(1.35); opacity:.55; } }
    .zv-enter { animation: zv-in-right .38s cubic-bezier(.2,.9,.2,1) both; }
    .zv-back { animation: zv-in-left .32s cubic-bezier(.2,.9,.2,1) both; }
    .zv-bub { position: relative; backdrop-filter: blur(16px) saturate(170%); -webkit-backdrop-filter: blur(16px) saturate(170%); }
    .zv-bub::after { content:""; position:absolute; inset:0; border-radius:inherit; pointer-events:none;
      background: linear-gradient(125deg, rgba(255,255,255,.38), rgba(255,255,255,0) 38%, rgba(255,255,255,0) 70%, rgba(255,255,255,.12)); }
    .zv-bub-mine { color:#fff; border:1px solid rgba(255,255,255,.38);
      background: linear-gradient(135deg, rgb(var(--brand-light-rgb,10 132 255) / .72), rgb(var(--brand-rgb,10 108 240) / .58));
      box-shadow: 0 10px 28px -10px rgb(var(--brand-rgb,10 108 240) / .65), inset 0 1px 0 rgba(255,255,255,.5), 0 0 0 1px rgb(var(--brand-light-rgb,10 132 255) / .15); }
    .zv-bub-other { color: rgb(var(--brand-ink-rgb,7 26 61)); border:1px solid rgba(255,255,255,.75);
      background: rgba(255,255,255,.58);
      box-shadow: 0 10px 28px -12px rgba(15,23,42,.35), inset 0 1px 0 rgba(255,255,255,.95); }
    .zv-bub a { text-decoration: underline; text-underline-offset: 2px; word-break: break-all; }
    .zv-rec-dot { width:10px; height:10px; border-radius:9999px; background:#ef4444; animation: zv-rec 1s infinite; }
  `}</style>
);

// ---------- Fondos por chat ----------
export const CHAT_BGS = [
  { id: "none", name: "Clásico", css: "linear-gradient(180deg,#f4f7fb,#eef2f8)" },
  { id: "aurora", name: "Aurora", css: "radial-gradient(at 15% 20%,rgba(10,132,255,.35),transparent 45%),radial-gradient(at 85% 75%,rgba(168,85,247,.32),transparent 45%),linear-gradient(180deg,#eef4ff,#f6f0ff)" },
  { id: "noche", name: "Noche", css: "radial-gradient(at 80% 10%,rgba(10,108,240,.45),transparent 50%),linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px) 0 0/28px 28px,linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px) 0 0/28px 28px,#071430" },
  { id: "menta", name: "Menta", css: "radial-gradient(at 20% 80%,rgba(16,185,129,.3),transparent 45%),linear-gradient(180deg,#ecfdf5,#f0fdfa)" },
  { id: "atardecer", name: "Atardecer", css: "radial-gradient(at 80% 20%,rgba(251,146,60,.4),transparent 45%),radial-gradient(at 15% 85%,rgba(236,72,153,.3),transparent 45%),linear-gradient(180deg,#fff7ed,#fdf2f8)" },
  { id: "puntos", name: "Puntos", css: "radial-gradient(rgba(10,108,240,.18) 1.2px,transparent 1.2px) 0 0/18px 18px,#f8fafc" },
  { id: "rosa", name: "Rosa", css: "radial-gradient(at 50% 0%,rgba(244,114,182,.35),transparent 55%),linear-gradient(180deg,#fdf2f8,#fff1f2)" },
];
const bgKey = (id) => `zv_chat_bg_${id}`;
export const getChatBg = (convId) => {
  try {
    const v = localStorage.getItem(bgKey(convId)) || "none";
    if (v.startsWith("data:")) return { css: `url(${v}) center/cover no-repeat`, dark: true };
    const b = CHAT_BGS.find((x) => x.id === v) || CHAT_BGS[0];
    return { css: b.css, dark: b.id === "noche", id: b.id };
  } catch { return { css: CHAT_BGS[0].css }; }
};

export function BgPicker({ convId, onChange, onClose }) {
  const fileRef = useRef(null);
  const current = (() => { try { return localStorage.getItem(bgKey(convId)) || "none"; } catch { return "none"; } })();
  const pick = (v) => { try { localStorage.setItem(bgKey(convId), v); } catch { toast.error("No se pudo guardar el fondo"); } onChange(); };
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, 1200 / Math.max(img.width, img.height));
      const c = document.createElement("canvas"); c.width = img.width * s; c.height = img.height * s;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      pick(c.toDataURL("image/jpeg", 0.72)); URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(f);
  };
  return (
    <div className="absolute top-14 right-2 z-[60] w-72 bg-white rounded-2xl shadow-2xl border border-nardo-200 p-3 zv-enter" data-testid="chat-bg-picker" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-navy">Fondo de este chat</span><button onClick={onClose} className="text-xs text-nardo-500">Cerrar</button></div>
      <div className="grid grid-cols-4 gap-2">
        {CHAT_BGS.map((b) => (
          <button key={b.id} onClick={() => pick(b.id)} data-testid={`chat-bg-${b.id}`} title={b.name}
            className={`relative h-14 rounded-xl border-2 transition-transform hover:scale-105 ${current === b.id ? "border-navy" : "border-transparent"}`} style={{ background: b.css }}>
            {current === b.id && <Check className="w-4 h-4 text-navy absolute top-1 right-1 bg-white rounded-full p-0.5" />}
            <span className="absolute bottom-0.5 inset-x-0 text-[9px] font-semibold text-center text-nardo-700 bg-white/70 rounded-b-lg">{b.name}</span>
          </button>
        ))}
        <button onClick={() => fileRef.current?.click()} data-testid="chat-bg-upload" className={`h-14 rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-[9px] text-navy ${current.startsWith("data:") ? "border-navy" : "border-nardo-300"}`}>
          <ImageIcon className="w-4 h-4" /> Mi foto
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      <p className="text-[10px] text-nardo-400 mt-2">Solo tú ves este fondo en este chat.</p>
    </div>
  );
}

// ---------- Texto con links que se pueden tocar ----------
const URL_RE = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]"'])/gi;
export const Linkify = ({ text }) => {
  const parts = String(text || "").split(URL_RE);
  return parts.map((p, i) => (i % 2 === 1
    ? <a key={i} href={p.startsWith("http") ? p : `https://${p}`} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>{p}</a>
    : <React.Fragment key={i}>{p}</React.Fragment>));
};

// ---------- Grabadora de audio (nota de voz) ----------
export function VoiceRecorder({ onSend, disabled }) {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const chunks = useRef([]);
  const timer = useRef(null);
  const cancelled = useRef(false);
  useEffect(() => () => { clearInterval(timer.current); rec?.stream?.getTracks().forEach((t) => t.stop()); }, [rec]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error("Tu navegador no permite grabar audio"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = []; cancelled.current = false;
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        if (cancelled.current || !chunks.current.length) return;
        const mime = (mr.mimeType || "audio/webm").split(";")[0];
        const blob = new Blob(chunks.current, { type: mime });
        onSend(new File([blob], `nota-de-voz.${mime.includes("mp4") ? "m4a" : "webm"}`, { type: mime }));
      };
      mr.start(); mr.stream = stream; setRec(mr); setSecs(0);
      timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch { toast.error("Permite el uso del micrófono para grabar"); }
  };
  const stop = (cancel) => { cancelled.current = cancel; rec?.state !== "inactive" && rec?.stop(); setRec(null); };

  if (!rec) {
    return (
      <button onClick={start} disabled={disabled} data-testid="chat-mic" title="Grabar nota de voz"
        className="shrink-0 w-11 h-11 flex items-center justify-center bg-gradient-to-br from-navy-light to-navy text-white rounded-full hover:scale-105 active:scale-95 disabled:opacity-40 transition-transform shadow-lg shadow-navy/30">
        <Mic className="w-5 h-5" />
      </button>
    );
  }
  const mm = String(Math.floor(secs / 60)).padStart(1, "0"); const ss = String(secs % 60).padStart(2, "0");
  return (
    <div className="flex-1 flex items-center gap-2 zv-enter" data-testid="chat-recording">
      <button onClick={() => stop(true)} data-testid="chat-rec-cancel" title="Cancelar" className="w-10 h-10 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-5 h-5" /></button>
      <div className="flex-1 flex items-center gap-2 bg-white/80 border border-nardo-200 rounded-full px-4 py-2.5">
        <span className="zv-rec-dot" /><span className="text-sm font-mono text-navy">{mm}:{ss}</span><span className="text-xs text-nardo-400">Grabando…</span>
        <Square className="w-3 h-3 text-nardo-300 ml-auto" />
      </div>
      <button onClick={() => stop(false)} data-testid="chat-rec-send" title="Enviar" className="w-11 h-11 rounded-full bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center hover:scale-105"><Send className="w-4 h-4" /></button>
    </div>
  );
}
